import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, FileSpreadsheet, FileArchive, RefreshCw, Tag, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../ui/button';
import { DEFAULT_CATEGORY_CONFIG } from '../ExpensesPanel';
import { DetailFilters } from './DetailFilters';
import { DetailKpis, eur } from './DetailKpis';
import { MonthlyChart, VendorRanking, CategoryBreakdown, monthLabel } from './DetailCharts';
import { DetailTable } from './DetailTable';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('admin_token')}` });

const downloadFile = async (url, filename) => {
  const res = await fetch(url, { headers: authHeaders() });
  if (!res.ok) {
    const j = await res.json().catch(() => ({}));
    throw new Error(j.detail || 'No se pudo descargar');
  }
  const blob = await res.blob();
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
};

export default function ExpenseCategoryDetail() {
  const params = useParams();
  const vendorName = params.name ? decodeURIComponent(params.name) : null;
  const slug = vendorName ? 'todas' : params.slug;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [filters, setFilters] = useState({
    fecha_inicio: searchParams.get('fecha_inicio') || '',
    fecha_fin: searchParams.get('fecha_fin') || '',
    proveedor: vendorName || searchParams.get('proveedor') || '',
    q: '',
  });
  const [data, setData] = useState(null);
  const [month, setMonth] = useState('');
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState('');
  const [config, setConfig] = useState(
    vendorName ? { label: vendorName, icon: Building2, color: '#0F766E' } : DEFAULT_CATEGORY_CONFIG[slug] || null
  );

  useEffect(() => {
    if (!vendorName) return;
    setFilters(f => ({ ...f, proveedor: vendorName }));
    setConfig({ label: vendorName, icon: Building2, color: '#0F766E' });
  }, [vendorName]);

  const qs = useMemo(() => {
    const p = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => v && p.set(k, v));
    return p.toString();
  }, [filters]);

  useEffect(() => setMonth(''), [qs]);
  const shownRows = useMemo(() => (data?.rows || []).filter(r => !month || (r.date || '').startsWith(month)), [data, month]);

  const fetchDetail = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/gastos/categoria/${slug}/detalle?${qs}`, { headers: authHeaders() });
      if (!res.ok) throw new Error();
      setData(await res.json());
    } catch {
      toast.error('Error al cargar el detalle');
    } finally {
      setLoading(false);
    }
  }, [slug, qs]);

  useEffect(() => {
    const t = setTimeout(fetchDetail, 250);
    return () => clearTimeout(t);
  }, [fetchDetail]);

  useEffect(() => {
    if (config) return;
    fetch(`${API_URL}/api/admin/gastos/categorias`, { headers: authHeaders() })
      .then(r => r.json())
      .then(j => {
        const c = (j.custom || []).find(x => x.slug === slug);
        setConfig({ label: c?.nombre || slug, icon: Tag, color: c?.color || '#6B7280' });
      })
      .catch(() => setConfig({ label: slug, icon: Tag, color: '#6B7280' }));
  }, [slug, config]);

  const handleDownload = async (kind) => {
    setDownloading(kind);
    try {
      const file = kind === 'excel' ? `excel` : `facturas.zip`;
      const base = vendorName ? vendorName.replace(/[^\w-]+/g, '_') : slug;
      await downloadFile(`${API_URL}/api/admin/gastos/categoria/${slug}/${file}?${qs}`,
        kind === 'excel' ? `gastos-${base}.xlsx` : `facturas-${base}.zip`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setDownloading('');
    }
  };

  const Icon = config?.icon || Tag;
  const color = config?.color || '#6B7280';

  return (
    <div className="space-y-4 p-4 md:p-6" data-testid="expense-category-detail">
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate(vendorName ? '/admin/gastos/proveedores' : '/admin/gastos')} data-testid="expense-detail-back-btn">
          <ArrowLeft size={20} />
        </Button>
        <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${color}20` }}>
          <Icon size={22} style={{ color }} />
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-bold text-gray-900" data-testid="expense-detail-title">{config?.label || slug}</h1>
          <p className="text-sm text-gray-500" data-testid="expense-detail-subtitle">
            {vendorName ? `Ficha de proveedor${data?.rows[0]?.vendor_nif ? ` · ${data.rows[0].vendor_nif}` : ''}` : 'Desglose completo con facturas'}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => handleDownload('excel')} disabled={!!downloading || !data?.rows.length} data-testid="expense-detail-excel-btn">
            {downloading === 'excel' ? <RefreshCw size={16} className="animate-spin mr-2" /> : <FileSpreadsheet size={16} className="mr-2 text-green-700" />}
            Excel
          </Button>
          <Button variant="outline" onClick={() => handleDownload('zip')} disabled={!!downloading || !data?.totals.with_invoice} data-testid="expense-detail-zip-btn">
            {downloading === 'zip' ? <RefreshCw size={16} className="animate-spin mr-2" /> : <FileArchive size={16} className="mr-2 text-amber-700" />}
            Facturas (.zip)
          </Button>
        </div>
      </div>

      <DetailFilters filters={filters} setFilters={setFilters} vendorOptions={data?.vendor_options || []} hideVendor={!!vendorName} />

      {loading && !data ? (
        <div className="flex justify-center py-16"><RefreshCw className="animate-spin text-gray-400" /></div>
      ) : data && (
        <>
          <DetailKpis totals={data.totals} color={color} />
          <div className="grid lg:grid-cols-5 gap-4">
            <div className="lg:col-span-3"><MonthlyChart monthly={data.monthly} color={color} selected={month} onSelect={(m) => setMonth(cur => (cur === m ? '' : m))} /></div>
            <div className="lg:col-span-2">
              {vendorName ? (
                <CategoryBreakdown byCategory={data.by_category || {}} />
              ) : (
                <VendorRanking vendors={data.vendors} color={color}
                  onSelect={(v) => setFilters(f => ({ ...f, proveedor: f.proveedor === v ? '' : v }))}
                  selected={filters.proveedor} />
              )}
            </div>
          </div>
          {month && (
            <div className="flex items-center gap-2 text-sm" data-testid="expense-detail-month-active">
              <span className="px-3 py-1 rounded-full text-white font-medium" style={{ backgroundColor: color }}>{monthLabel(month)}</span>
              <span className="text-gray-600">{shownRows.length} factura{shownRows.length === 1 ? '' : 's'} · <b>{eur(shownRows.reduce((t, r) => t + (Number(r.amount) || 0), 0))}</b></span>
              <button onClick={() => setMonth('')} className="text-blue-700 underline" data-testid="expense-detail-month-clear">Ver todos los meses</button>
            </div>
          )}
          <DetailTable rows={shownRows} onDeleted={fetchDetail} />
        </>
      )}
    </div>
  );
}
